import { Delete, Edit, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import Price from "../../common/Price";
import { SpecificProductPricing } from "../../../types/admin";

interface ProductPricingTableProps {
  products: SpecificProductPricing[];
  loading: boolean;
  onEdit: (product: SpecificProductPricing) => void;
  onDelete: (id: string) => void;
}

const ProductPricingTable: React.FC<ProductPricingTableProps> = ({
  products,
  loading,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {t("admin.pricing.noProductPricingRules")}
        </Typography>
      </Box>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.50" }}>
            <TableCell sx={{ fontWeight: "bold" }}>{t("admin.pricing.productKeyword")}</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>{t("admin.pricing.category")}</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>{t("admin.pricing.fixedPrice")}</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>{t("admin.pricing.description")}</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>{t("admin.pricing.status")}</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>{t("admin.pricing.created")}</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>{t("common.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product) => (
            <TableRow key={product.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {product.productKeyword}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={product.categoryName}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  <Price value={product.fixedPrice} disableCentsHeuristic />
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 200 }}
                >
                  {product.description || "-"}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={product.isActive ? t("admin.pricing.active") : t("admin.pricing.inactive")}
                  size="small"
                  color={product.isActive ? "success" : "default"}
                  icon={product.isActive ? <Visibility /> : <VisibilityOff />}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(product.createdAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={t("common.edit")}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onEdit(product)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("common.delete")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(product.id)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProductPricingTable;
